// src/services/auth.ts
import apiClient, {useApiBaseUrl} from '.';
import {logoutAction} from '../redux/rootAction';
import {resetAnalyticUserState} from '../redux/slices/analyticsSlice';
import {persistor} from '../redux/store';
import {clearLocalSessionStorage, removeAuthToken} from '../utils/auth';
import {Dispatch} from '@reduxjs/toolkit';
import {resetQualtricsData} from '../utils/qualtricsUtils';

const version = 'v1';
const routePrefix = '/Authentication';
const accountLoginUrl = '/account/login';
const accountUserInfoUrl = '/account/userInfo';
const tokenUrl = '/account/token';
const logoutUrl = '/account/logout';

const buildUrl = (relative: string) => {
  return `${version}${routePrefix}${relative}`;
};

const urlsV1 = {
  login: buildUrl(accountLoginUrl),
  logout: buildUrl(logoutUrl),
  userInfo: buildUrl(accountUserInfoUrl),
  token: buildUrl(tokenUrl),
};

/**
 * Initiates a redirect to the authentication login endpoint to begin oidc.
 * @param email The submitted email address
 */
export const login = async (email: string, redirectData?: any) => {
  const encodedEmail = encodeURIComponent(email);
  const targetUrl = useApiBaseUrl(
    `${urlsV1.login}?login_hint=${encodedEmail}${
      redirectData ? `&redirect_data=${encodeURIComponent(redirectData)}` : ''
    }`
  );
  window.location.assign(targetUrl);
};

/**
 * Request the userinfo for the current authenticated session.
 * @returns The user info
 */
export const userInfo = async () => {
  const response = await apiClient.get(`${urlsV1.userInfo}`, {
    withCredentials: true,
  });
  return response.data;
};

/**
 * Request token from bff-token endpoint.
 * IETF: https://www.ietf.org/archive/id/draft-bertocci-oauth2-tmi-bff-01.html
 */
export const requestMediatedToken = async () => {
  const response = await apiClient.get(`${urlsV1.token}`, {
    withCredentials: true,
  });
  return response.data;
};

/**
 * Initiates logout
 */
export const logout = async (dispatch?: Dispatch) => {
  try {
    if (dispatch) dispatch(logoutAction());
    const response = await apiClient.get(`${urlsV1.logout}`, {
      withCredentials: true,
    });
    return response.data;
  } finally {
    removeAuthToken();
    if (dispatch) {
      dispatch(resetAnalyticUserState());
    }
    resetQualtricsData();

    await persistor.purge();
    localStorage.removeItem('persist:root');
    clearLocalSessionStorage();
  }
};
